import React, { HTMLAttributes, useState } from 'react';
import { apiGetPolicyDocumentDetailInfo } from '@/dashboard/api/policy';
import { ReactComponent as PDFIcon } from '@/dashboard/assets/images/policy/img_download_file.svg';
import LogoLoading from '@/dashboard/components/loading/LogoLoading';
import { useDownLoadFile } from '@/dashboard/hooks/useDownloadFile';
import { DownloadIcon } from '@heroicons/react/outline';
import { Box, IconButton, Typography } from '@mui/material';
import clsx from 'clsx';
import toast from 'react-hot-toast';

type DownloadFileViewProps = HTMLAttributes<HTMLDivElement> & {
  info: {
    revisionId?: string;
    title?: string;
    type?: number;
    iconUrl?: string;
    url?: string;
    fileSizeText?: number;
    updateTimeText?: string;
  };
};

const DownloadFileView = (props: DownloadFileViewProps) => {
  const { info: { title, url, fileSizeText, updateTimeText, revisionId } = {}, className } = props;
  const { downloadIndividualFile, downloadBlobFile } = useDownLoadFile();
  const [loading, setLoading] = useState(false);

  /**
   * requests
   */
  const requestDocumentInfo = async () => {
    setLoading(true);
    const res = await apiGetPolicyDocumentDetailInfo({
      revision_id: revisionId,
    });
    setLoading(false);

    downloadBlobFile(res);
  };

  /**
   * methods
   */
  const downloadUrlAction = () => {
    if (revisionId) {
      requestDocumentInfo();
    } else {
      downloadFile(url);
    }
  };

  const downloadFile = (fileUrl?: string) => {
    if (fileUrl) {
      downloadIndividualFile(fileUrl);
    } else {
      toast.error('The file address is abnormal, please try again.');
    }
  };

  return (
    <Box className={clsx('relative flex h-[68px] flex-row items-center justify-between rounded-lg px-3', className)}>
      {loading && <LogoLoading size="small" />}
      <Box className="flex w-0 flex-1 flex-row items-center">
        <Box className="border-grey-20 flex h-10 w-10 items-center justify-center rounded-lg border">
          <PDFIcon className="h-5 w-5" />
        </Box>
        <Box className="flex-c ml-3 w-0 flex-1">
          <Typography className="v-ellipsis text-black-secondary font-bold" variant="subtitle1">
            {title}
          </Typography>
          <Typography className="text-black-secondary font-medium" variant="subtitle2">
            {fileSizeText}MB • {updateTimeText}
          </Typography>
        </Box>
      </Box>
      {(url || revisionId) && (
        <IconButton onClick={downloadUrlAction}>
          <DownloadIcon className="h-6 w-6" />
        </IconButton>
      )}
    </Box>
  );
};

export default DownloadFileView;
